import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { documentService } from '@/services'

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error'

interface UploadState {
  status: UploadStatus
  fileName: string | null
  documentId: string | null
  error: string | null
}

/**
 * Hook to handle document file selection and upload.
 * Triggers a hidden file input, uploads via DocumentService,
 * and navigates to the analyzing page on success.
 */
export function useDocumentUpload() {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [state, setState] = useState<UploadState>({
    status: 'idle',
    fileName: null,
    documentId: null,
    error: null,
  })

  /** Infer document type from file MIME type. */
  const inferType = (file: File): string => {
    const mime = file.type
    if (mime === 'application/pdf') return 'pdf'
    if (mime.startsWith('audio/')) return 'audio'
    if (
      mime === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      mime === 'application/vnd.ms-powerpoint'
    )
      return 'pptx'
    return 'text'
  }

  /** Open the native file picker. */
  const triggerFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  /** Upload a file and navigate to the analyzing page on success. */
  const uploadFile = useCallback(
    async (file: File, labels?: string[]) => {
      setState({ status: 'uploading', fileName: file.name, documentId: null, error: null })

      try {
        const type = inferType(file)
        const res = await documentService.uploadDocument(file, type, file.name, labels)
        setState({ status: 'success', fileName: file.name, documentId: res.documentId, error: null })
        navigate(`/analyzing/${res.documentId}`)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed'
        setState((prev) => ({ ...prev, status: 'error', error: message }))
      }
    },
    [navigate]
  )

  /** Handle the <input type="file"> change event. */
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, labels?: string[]) => {
      const file = e.target.files?.[0]
      if (file) uploadFile(file, labels)
      // Reset input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [uploadFile]
  )

  const reset = useCallback(() => {
    setState({ status: 'idle', fileName: null, documentId: null, error: null })
  }, [])

  return {
    ...state,
    fileInputRef,
    triggerFilePicker,
    uploadFile,
    handleFileChange,
    reset,
  }
}
